/**
 * UK calendar helpers for tracking filters.
 *
 * Date pickers store YYYY-MM-DD. Those strings are Europe/London civil days,
 * not UTC days. This module is the only place that converts them to instants.
 */
export const UK_TIME_ZONE = 'Europe/London';

export function formatYmdInZone(date, timeZone = UK_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const g = (type) => parts.find((part) => part.type === type).value;
  return `${g('year')}-${g('month')}-${g('day')}`;
}

export function ukTodayYmd(now = new Date()) {
  return formatYmdInZone(now, UK_TIME_ZONE);
}

export function toInputDate(date) {
  return formatYmdInZone(date, UK_TIME_ZONE);
}

export function addCalendarDays(ymd, days) {
  const [year, month, day] = String(ymd).split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days, 12)).toISOString().slice(0, 10);
}

export function defaultDateRange(days = 7, now = new Date()) {
  const toDate = ukTodayYmd(now);
  return {
    fromDate: addCalendarDays(toDate, -(days - 1)),
    toDate
  };
}

export function todayDateRange(now = new Date()) {
  const today = ukTodayYmd(now);
  return { fromDate: today, toDate: today };
}

/**
 * Convert UK calendar dates to inclusive instants (ISO-8601 UTC).
 * from = UK midnight on fromDate, to = 1ms before UK midnight on the next day.
 */
export function rangeParams(fromDate, toDate) {
  return {
    from: ukDayStartIso(fromDate),
    to: ukDayEndIso(toDate)
  };
}

export function ukDayStartIso(ymd) {
  if (!ymd) return undefined;
  return new Date(ukMidnightUtcMs(ymd)).toISOString();
}

export function ukDayEndIso(ymd) {
  if (!ymd) return undefined;
  return new Date(ukMidnightUtcMs(addCalendarDays(ymd, 1)) - 1).toISOString();
}

export function mondayOfUkWeek(ymd) {
  const [year, month, day] = String(ymd).split('-').map(Number);
  const utcNoon = new Date(Date.UTC(year, month - 1, day, 12));
  const weekday = utcNoon.getUTCDay();
  const offset = weekday === 0 ? -6 : 1 - weekday;
  return addCalendarDays(ymd, offset);
}

/** Inclusive day count between two YYYY-MM-DD strings. */
export function spanDaysInclusive(fromDate, toDate) {
  if (!fromDate || !toDate) {
    return 1;
  }
  let from = String(fromDate);
  const to = String(toDate);
  if (from > to) {
    return 1;
  }
  let days = 0;
  let cur = from;
  while (cur <= to) {
    days += 1;
    if (cur === to) {
      break;
    }
    cur = addCalendarDays(cur, 1);
  }
  return days;
}

/** Slide the current range forward or back by its own length (e.g. 7 days → previous/next week). */
export function shiftDateRange(fromDate, toDate, direction) {
  const span = spanDaysInclusive(fromDate, toDate);
  const delta = direction * span;
  return {
    fromDate: addCalendarDays(fromDate, delta),
    toDate: addCalendarDays(toDate, delta)
  };
}

/** False when shifting forward would end after UK today. */
export function canShiftRangeForward(fromDate, toDate, now = new Date()) {
  if (!fromDate || !toDate) {
    return false;
  }
  const today = ukTodayYmd(now);
  const span = spanDaysInclusive(fromDate, toDate);
  return addCalendarDays(toDate, span) <= today;
}

export function datePresets(now = new Date()) {
  const today = ukTodayYmd(now);
  const lastN = (n) => ({
    fromDate: addCalendarDays(today, -(n - 1)),
    toDate: today
  });
  const thisMonday = mondayOfUkWeek(today);
  const thisSunday = addCalendarDays(thisMonday, 6);
  const weekAgoMonday = addCalendarDays(thisMonday, -7);
  const weekAgoSunday = addCalendarDays(weekAgoMonday, 6);
  const twoAgoMonday = addCalendarDays(thisMonday, -14);
  const twoAgoSunday = addCalendarDays(twoAgoMonday, 6);
  const threeAgoMonday = addCalendarDays(thisMonday, -21);
  const threeAgoSunday = addCalendarDays(threeAgoMonday, 6);

  return [
    { id: 'today', label: 'Today', hint: 'UK today (Europe/London), midnight to midnight', ...lastN(1) },
    { id: '7d', label: 'Last 7 days', hint: 'Last 7 UK calendar days including today', ...lastN(7) },
    { id: '14d', label: 'Last 14 days', hint: 'Last 14 UK calendar days including today', ...lastN(14) },
    { id: 'week1', label: 'This week', hint: 'Monday–Sunday of the current UK week', fromDate: thisMonday, toDate: thisSunday > today ? today : thisSunday },
    { id: 'week2', label: 'Last week', hint: 'Previous UK Monday–Sunday', fromDate: weekAgoMonday, toDate: weekAgoSunday },
    { id: 'week3', label: '2 weeks ago', hint: 'The UK week before last (Mon–Sun)', fromDate: twoAgoMonday, toDate: twoAgoSunday },
    { id: 'week4', label: '3 weeks ago', hint: 'Three UK weeks ago (Mon–Sun)', fromDate: threeAgoMonday, toDate: threeAgoSunday }
  ];
}

/** Longer ranges for the date picker, alongside datePresets (UK calendar days). */
export function largerDatePresets(now = new Date()) {
  const today = ukTodayYmd(now);
  const [year, month] = today.split('-').map(Number);
  const pad = (n) => String(n).padStart(2, '0');
  const firstOfMonth = `${year}-${pad(month)}-01`;
  const lastMonthEnd = addCalendarDays(firstOfMonth, -1);
  const lastMonthStart = `${lastMonthEnd.slice(0, 7)}-01`;
  return [
    { id: 'yesterday', label: 'Yesterday', hint: 'The previous UK calendar day', fromDate: addCalendarDays(today, -1), toDate: addCalendarDays(today, -1) },
    { id: '30d', label: 'Last 30 days', hint: 'Last 30 UK calendar days including today', fromDate: addCalendarDays(today, -29), toDate: today },
    { id: '90d', label: 'Last 90 days', hint: 'Last 90 UK calendar days including today', fromDate: addCalendarDays(today, -89), toDate: today },
    { id: 'month1', label: 'This month', hint: '1st of this month to today (UK)', fromDate: firstOfMonth, toDate: today },
    { id: 'month2', label: 'Last month', hint: 'The whole previous calendar month (UK)', fromDate: lastMonthStart, toDate: lastMonthEnd }
  ];
}

/**
 * True when instant is inside the inclusive UK day window used by the dashboard.
 */
export function isInUkRange(instantIso, fromDate, toDate) {
  const t = new Date(instantIso).getTime();
  const from = new Date(ukDayStartIso(fromDate)).getTime();
  const to = new Date(ukDayEndIso(toDate)).getTime();
  return t >= from && t <= to;
}

function tzOffsetMs(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(date);
  const g = (type) => Number(parts.find((part) => part.type === type).value);
  let hour = g('hour');
  if (hour === 24) {
    hour = 0;
  }
  const asUtc = Date.UTC(g('year'), g('month') - 1, g('day'), hour, g('minute'), g('second'));
  const truncated = Math.floor(date.getTime() / 1000) * 1000;
  return asUtc - truncated;
}

function ukMidnightUtcMs(ymd) {
  const [year, month, day] = String(ymd).split('-').map(Number);
  let utc = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
  for (let i = 0; i < 3; i++) {
    const offset = tzOffsetMs(new Date(utc), UK_TIME_ZONE);
    utc = Date.UTC(year, month - 1, day, 0, 0, 0, 0) - offset;
  }
  return utc;
}
